import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  createItemsAlreadyExistToast,
  MoveDuplicateItemsActionSheet,
} from './move-duplicate-items-action-sheet';
import {
  EditOrderGuideActions,
  Partition,
  validateExistingItems,
} from '@/hooks/use-edit-order-guide-reducer';
import { ListOption } from '@/components/list-item';
import { NO_PARTITION_NAME } from '@/contexts/multi-order-guide-items-context-provider';
import { Type } from '@/components/toast';
import { useEditOrderGuideActions } from '@/contexts/edit-order-guide-context';
import { useToasts } from '@/contexts/toast-context';

enum MoveDirection {
  Bottom = 'BOTTOM',
  Top = 'TOP',
}

interface Props {
  onClose: (deselectOnComplete: boolean) => void;
  partitions: Partition[];
  selectedItemUUIDs: string[];
  selectedPartitionName: string;
  selectedPartitionUUID: string;
  sortingEnabled: boolean;
}

export const MoveItemsActionSheet: React.FC<Props> = ({
  onClose,
  partitions,
  selectedItemUUIDs,
  selectedPartitionName,
  selectedPartitionUUID,
  sortingEnabled,
}) => {
  const dispatch = useEditOrderGuideActions();

  const { showToast } = useToasts();

  const sections = useMemo(() => {
    const partitionOptions = partitions.reduce<ListOption[]>((result, p) => {
      if (p.partitionUUID !== selectedPartitionUUID) {
        result.push({
          title: p.partitionName || NO_PARTITION_NAME,
          value: p.partitionUUID,
        });
      }

      return result;
    }, []);

    if (!sortingEnabled) {
      return [{ data: partitionOptions }];
    }

    return [
      {
        data: [
          {
            title: i18n.t('top_of_group', {
              groupName: selectedPartitionName,
            }),
            value: MoveDirection.Top,
          },
          {
            title: i18n.t('bottom_of_group', {
              groupName: selectedPartitionName,
            }),
            value: MoveDirection.Bottom,
          },
        ],
      },
      { data: partitionOptions, key: i18n.t('another_group') },
    ];
  }, [
    partitions,
    selectedPartitionName,
    selectedPartitionUUID,
    sortingEnabled,
  ]);

  return (
    <MoveDuplicateItemsActionSheet
      dispatch={dispatch}
      onClose={() => onClose(false)}
      onSelect={(option) => {
        let deselectOnComplete = false;
        switch (option.value) {
          case MoveDirection.Bottom:
            dispatch({
              payload: {
                itemUUIDs: selectedItemUUIDs,
                partitionUUID: selectedPartitionUUID,
              },
              type: EditOrderGuideActions.MOVE_ITEMS_TO_BOTTOM,
            });
            break;
          case MoveDirection.Top:
            dispatch({
              payload: {
                itemUUIDs: selectedItemUUIDs,
                partitionUUID: selectedPartitionUUID,
              },
              type: EditOrderGuideActions.MOVE_ITEMS_TO_TOP,
            });
            break;
          default: {
            dispatch((actionDispatch, getState) => {
              const targetPartitionUUID = option.value as string;
              const [itemUUIDs, existingItems] = validateExistingItems(
                getState(),
                selectedItemUUIDs,
                selectedPartitionUUID,
                targetPartitionUUID,
              );

              if (existingItems.length) {
                showToast(
                  createItemsAlreadyExistToast(existingItems, option.title),
                );
              }

              if (itemUUIDs.length) {
                actionDispatch({
                  payload: {
                    itemUUIDs,
                    sourcePartitionUUID: selectedPartitionUUID,
                    targetPartitionUUID,
                  },
                  type: EditOrderGuideActions.MOVE_ITEMS,
                });

                showToast({
                  content: i18n.t('toast.move_items_success', {
                    count: itemUUIDs.length,
                    groupName: option.title,
                  }),
                  type: Type.POSITIVE,
                });
                deselectOnComplete = true;
              }
            });
            break;
          }
        }

        onClose(deselectOnComplete);
      }}
      sections={sections}
      selectPartitionTitle={i18n.t('move_items_to')}
    />
  );
};
